import React, { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { 
  Loader2, Users, TrendingUp, Gift, Copy, Check, 
  Clock, DollarSign, Crown, X, BarChart3, Share2
} from "lucide-react";
import { apiClient } from "../App";
import { toast } from "sonner";

const ReferralDashboard = ({ isOpen, onClose, user }) => {
  const [loading, setLoading] = useState(true);
  const [userReferralData, setUserReferralData] = useState(null);
  const [influencerData, setInfluencerData] = useState(null);
  const [activeTab, setActiveTab] = useState('referral'); // 'referral' or 'influencer'
  const [copied, setCopied] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch both user referral and influencer data in parallel
      const [referralRes, influencerRes] = await Promise.all([
        apiClient.get('/user/referral-dashboard'),
        apiClient.get('/user/influencer-dashboard')
      ]);
      
      setUserReferralData(referralRes.data);
      setInfluencerData(influencerRes.data);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      toast.error('Failed to load referral data');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchDashboardData();
    }
  }, [isOpen, fetchDashboardData]);

  const copyReferralCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareReferralCode = async (code) => {
    const shareText = `Join AlphaTradeVission with my referral code: ${code} and get started with real-time tick data!`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join AlphaTradeVission',
          text: shareText,
        });
      } catch (err) {
        copyReferralCode(code);
      }
    } else {
      copyReferralCode(code);
    }
  };

  if (!isOpen) return null;

  // Check if user has access
  const canViewReferral = userReferralData?.can_view;
  const canViewInfluencer = influencerData?.can_view;
  const isInfluencer = influencerData?.is_influencer;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-[#0a0e17] border border-[#1a2540] rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#1a2540] sticky top-0 bg-[#0a0e17] z-10">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-green-400" />
            My Referral Dashboard
          </h2>
          <button onClick={onClose} className="text-[#6b7a99] hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-green-400" />
            </div>
          ) : !canViewReferral && !canViewInfluencer ? (
            <div className="text-center py-12">
              <BarChart3 className="w-12 h-12 text-[#4a5a7a] mx-auto mb-4" />
              <p className="text-[#6b7a99] text-sm">
                Referral dashboard is not enabled for your account.
              </p>
              <p className="text-[#4a5a7a] text-xs mt-2">
                Contact admin to request access.
              </p>
            </div>
          ) : (
            <>
              {/* Tabs for User Referral vs Influencer */}
              {isInfluencer && (
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setActiveTab('referral')}
                    className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                      activeTab === 'referral'
                        ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                        : 'text-[#6b7a99] hover:text-white hover:bg-[#111827]'
                    }`}
                  >
                    <Users className="w-3 h-3 inline mr-1" />
                    User Referrals
                  </button>
                  <button
                    onClick={() => setActiveTab('influencer')}
                    className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                      activeTab === 'influencer'
                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/40'
                        : 'text-[#6b7a99] hover:text-white hover:bg-[#111827]'
                    }`}
                  >
                    <Crown className="w-3 h-3 inline mr-1" />
                    Influencer Stats
                  </button>
                </div>
              )}

              {/* User Referral Tab */}
              {activeTab === 'referral' && canViewReferral && userReferralData?.success && (
                <div className="space-y-4">
                  {/* Referral Code Card */}
                  <div className="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl">
                    <p className="text-xs text-green-400 mb-2">Your Referral Code</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xl font-bold text-white bg-[#0a0e17] px-4 py-2 rounded-lg">
                        {userReferralData.referral_code}
                      </code>
                      <Button
                        size="sm"
                        onClick={() => copyReferralCode(userReferralData.referral_code)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => shareReferralCode(userReferralData.referral_code)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Share2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-[10px] text-[#6b7a99] mt-2">
                      Share this code with friends. You get {userReferralData.stats?.reward_per_referral} for each paid subscription!
                    </p>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="p-3 bg-[#111827] border border-[#1a2540] rounded-lg text-center">
                      <Users className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                      <p className="text-xl font-bold text-white">{userReferralData.stats?.total_signups || 0}</p>
                      <p className="text-[10px] text-[#6b7a99]">Total Signups</p>
                    </div>
                    <div className="p-3 bg-[#111827] border border-[#1a2540] rounded-lg text-center">
                      <TrendingUp className="w-5 h-5 text-green-400 mx-auto mb-1" />
                      <p className="text-xl font-bold text-white">{userReferralData.stats?.successful_subscriptions || 0}</p>
                      <p className="text-[10px] text-[#6b7a99]">Paid Subscriptions</p>
                    </div>
                    <div className="p-3 bg-[#111827] border border-[#1a2540] rounded-lg text-center">
                      <Clock className="w-5 h-5 text-amber-400 mx-auto mb-1" />
                      <p className="text-xl font-bold text-white">{userReferralData.stats?.pending_signups || 0}</p>
                      <p className="text-[10px] text-[#6b7a99]">Pending</p>
                    </div>
                    <div className="p-3 bg-[#111827] border border-[#1a2540] rounded-lg text-center">
                      <Gift className="w-5 h-5 text-purple-400 mx-auto mb-1" />
                      <p className="text-xl font-bold text-white">{userReferralData.stats?.bonus_days_earned || 0}</p>
                      <p className="text-[10px] text-[#6b7a99]">Days Earned</p>
                    </div>
                  </div>

                  {/* Referred Users List */}
                  <div>
                    <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4 text-green-400" />
                      Referred Users ({userReferralData.referred_users?.length || 0})
                    </h3>
                    {userReferralData.referred_users?.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {userReferralData.referred_users.map((refUser, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-[#111827] rounded-lg">
                            <div>
                              <p className="text-sm text-white">{refUser.name}</p>
                              <p className="text-[10px] text-[#6b7a99]">{refUser.email}</p>
                            </div>
                            <span className={`text-[9px] px-2 py-0.5 rounded ${
                              refUser.subscription_status === 'active' 
                                ? 'bg-green-500/20 text-green-400'
                                : refUser.subscription_status === 'trial'
                                ? 'bg-blue-500/20 text-blue-400'
                                : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {refUser.subscription_status?.toUpperCase() || 'FREE'}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-[#6b7a99] text-center py-4 bg-[#111827] rounded-lg">
                        No referrals yet. Share your code to start earning!
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Influencer Tab */}
              {activeTab === 'influencer' && isInfluencer && influencerData?.success && (
                <div className="space-y-4">
                  {/* Influencer Info Card */}
                  <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-xs text-purple-400">Influencer Account</p>
                        <p className="text-lg font-bold text-white">{influencerData.influencer_details?.name}</p>
                      </div>
                      {influencerData.influencer_details?.is_lifetime_partner && (
                        <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-[10px] rounded-full">
                          Lifetime Partner
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <code className="flex-1 text-lg font-bold text-white bg-[#0a0e17] px-4 py-2 rounded-lg">
                        {influencerData.influencer_details?.promo_code}
                      </code>
                      <Button
                        size="sm"
                        onClick={() => copyReferralCode(influencerData.influencer_details?.promo_code)}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-[10px] text-[#6b7a99] mt-2">
                      Commission Rate: {influencerData.influencer_details?.commission_percent}% per sale
                    </p>
                  </div>

                  {/* Earnings Stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="p-3 bg-[#111827] border border-[#1a2540] rounded-lg text-center">
                      <Users className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                      <p className="text-xl font-bold text-white">{influencerData.stats?.total_referrals || 0}</p>
                      <p className="text-[10px] text-[#6b7a99]">Total Referrals</p>
                    </div>
                    <div className="p-3 bg-[#111827] border border-[#1a2540] rounded-lg text-center">
                      <TrendingUp className="w-5 h-5 text-green-400 mx-auto mb-1" />
                      <p className="text-xl font-bold text-white">Rs.{influencerData.stats?.total_revenue_generated || 0}</p>
                      <p className="text-[10px] text-[#6b7a99]">Revenue Generated</p>
                    </div>
                    <div className="p-3 bg-[#111827] border border-[#1a2540] rounded-lg text-center">
                      <DollarSign className="w-5 h-5 text-amber-400 mx-auto mb-1" />
                      <p className="text-xl font-bold text-white">Rs.{influencerData.stats?.total_commission_earned || 0}</p>
                      <p className="text-[10px] text-[#6b7a99]">Total Earned</p>
                    </div>
                  </div>

                  {/* Payment Status */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                      <p className="text-lg font-bold text-green-400">Rs.{influencerData.stats?.total_commission_paid || 0}</p>
                      <p className="text-[10px] text-[#6b7a99]">Paid Out</p>
                    </div>
                    <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-center">
                      <p className="text-lg font-bold text-amber-400">Rs.{influencerData.stats?.pending_commission || 0}</p>
                      <p className="text-[10px] text-[#6b7a99]">Pending Payout</p>
                    </div>
                  </div>

                  {/* Recent Transactions */}
                  <div>
                    <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-amber-400" />
                      Commission Transactions
                    </h3>
                    {influencerData.transactions?.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {influencerData.transactions.map((txn, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-[#111827] rounded-lg">
                            <div>
                              <p className="text-sm text-white">{txn.referee_email}</p>
                              <p className="text-[10px] text-[#6b7a99]">
                                Rs.{txn.payment_amount} x {txn.commission_percent}%
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-green-400">+Rs.{txn.commission_amount}</p>
                              <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                                txn.status === 'paid' 
                                  ? 'bg-green-500/20 text-green-400'
                                  : 'bg-amber-500/20 text-amber-400'
                              }`}>
                                {txn.status?.toUpperCase() || 'PENDING'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-[#6b7a99] text-center py-4 bg-[#111827] rounded-lg">
                        No commission transactions yet.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Not an influencer message */}
              {activeTab === 'influencer' && !isInfluencer && (
                <div className="text-center py-8">
                  <Crown className="w-12 h-12 text-[#4a5a7a] mx-auto mb-4" />
                  <p className="text-[#6b7a99] text-sm">
                    You are not registered as an influencer.
                  </p>
                  <p className="text-[#4a5a7a] text-xs mt-2">
                    Contact admin to become an affiliate partner and earn commissions!
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralDashboard;
